import React from 'react';
import styled from 'styled-components';
import { Fade, Slide } from 'react-awesome-reveal';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle } from '@/components/common';
import { useAuth } from '@/context';
import { useTenant } from '@core/tenant/TenantContext';
import { useDashboard, useDailyStatusDigest, formatAmount } from '@/api/dashboard';
import { formatRelativeTime } from '@/utils';
import { usePaymentDue } from '@/api/paymentDue';
import { PendingPaymentsWidget } from '@/components/dashboard/PendingPaymentsWidget';
import { AttendanceConcernsWidget } from '@/components/dashboard/AttendanceConcernsWidget';
import { StaffLeaveWidget } from '@/components/dashboard/StaffLeaveWidget';
import { LeaveApprovalsWidget } from '@/components/dashboard/LeaveApprovalsWidget';
import { DailyStatusDigestCard } from '@/components/dashboard/DailyStatusDigestCard';
import { useDigestFanOut } from '@/components/dashboard/useDigestFanOut';
import { mergeDailyStatusDigests } from '@/components/dashboard/mergeDailyStatusDigests';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const ContentGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const DigestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const WelcomeBanner = styled.div`
  background: ${({ theme }) => theme.colors.cardBgGradient};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  
  /* Gradient accent */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #FFD700 0%, #FF6B35 50%, #FFB347 100%);
  }
  
  /* Decorative elements */
  &::after {
    content: '';
    position: absolute;
    right: -50px;
    top: -50px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const WelcomeTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WelcomeSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin: 0;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 6px 12px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: #000;
  margin-left: ${({ theme }) => theme.spacing.md};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const StatCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.lg};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ theme }) => theme.colors.primaryGradient};
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: linear-gradient(135deg, ${({ $color }) => $color}20 0%, ${({ $color }) => $color}10 100%);
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  border: 2px solid ${({ $color }) => $color}30;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const StatChange = styled.div<{ $positive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-top: ${({ theme }) => theme.spacing.sm};
  background: ${({ $positive, theme }) => $positive ? theme.colors.successLight : theme.colors.errorLight};
  color: ${({ $positive, theme }) => $positive ? theme.colors.success : theme.colors.error};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActivityItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div<{ $type: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  ${({ $type, theme }) => {
    switch ($type) {
      case 'payment':
        return `background: ${theme.colors.successLight}; color: ${theme.colors.success};`;
      case 'enrollment':
        return `background: ${theme.colors.primaryLight}; color: ${theme.colors.primary};`;
      case 'booking':
        return `background: ${theme.colors.infoLight}; color: ${theme.colors.info};`;
      case 'lead':
        return `background: ${theme.colors.warningLight}; color: ${theme.colors.warning};`;
      default:
        return `background: ${theme.colors.backgroundTertiary}; color: ${theme.colors.textSecondary};`;
    }
  }}
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ActivityDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ActivityTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const SubjectList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SubjectItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const SubjectName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const SubjectStats = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Revenue highlight section for owners
const RevenueHighlight = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RevenueCard = styled(Card)`
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #FFD700, #FF6B35);
  }
`;

const RevenueValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  background: linear-gradient(135deg, #FFD700 0%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const RevenueLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const OwnerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading } = useDashboard();
  const { data: pendingPayments, isLoading: pendingPaymentsLoading } = usePaymentDue('this_month');

  // D9 — single tenant selected: one digest card, same as Admin. All mode:
  // fan out per-tenant + an additional client-side-summed consolidated
  // card. Never a cross-tenant backend query (see useDigestFanOut.ts).
  const { isAllMode, availableTenantIds } = useTenant();
  const today = todayIso();
  const singleDigest = useDailyStatusDigest(today);
  const fanOutResults = useDigestFanOut(isAllMode ? availableTenantIds : [], today);
  const fanOutDigests = fanOutResults.map((r) => r.data).filter((d): d is NonNullable<typeof d> => Boolean(d));
  const consolidatedDigest = isAllMode ? mergeDailyStatusDigests(fanOutDigests) : null;

  const stats = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
      ),
      color: '#FF6B35',
      label: 'Active Students',
      value: isLoading ? '—' : (data?.active_students ?? 0),
      change: '',
      positive: true,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
      color: '#22C55E',
      label: 'Sessions This Week',
      value: isLoading ? '—' : (data?.sessions_this_week ?? 0),
      change: '',
      positive: true,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      color: '#3B82F6',
      label: 'Active Classes',
      value: isLoading ? '—' : (data?.active_classes ?? 0),
      change: '',
      positive: true,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      color: '#FF8C42',
      label: 'Sessions Today',
      value: isLoading ? '—' : (data?.sessions_today ?? 0),
      change: '',
      positive: true,
    },
  ];

  return (
    <PageContainer
      title="Owner Dashboard"
      subtitle="Complete business overview and analytics"
    >
      <Fade triggerOnce>
        <WelcomeBanner>
          <WelcomeTitle>
            Welcome back, {user?.email?.split('@')[0]}!
            <RoleBadge>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Owner
            </RoleBadge>
          </WelcomeTitle>
          <WelcomeSubtitle>
            Here's a complete overview of Candeza's performance.
          </WelcomeSubtitle>
        </WelcomeBanner>
      </Fade>

      <Fade triggerOnce>
        <div style={{ marginBottom: 24 }}>
          <LeaveApprovalsWidget />
        </div>
      </Fade>

      {/* Revenue Section - Only visible to owners */}
      <Fade triggerOnce>
        <RevenueHighlight>
          <RevenueCard padding="lg">
            <RevenueValue>{isLoading ? '—' : formatAmount(data?.payments_this_month_total ?? 0)}</RevenueValue>
            <RevenueLabel>Payments This Month</RevenueLabel>
          </RevenueCard>
          <RevenueCard padding="lg">
            <RevenueValue>{isLoading ? '—' : formatAmount(data?.outstanding_invoices_total ?? 0)}</RevenueValue>
            <RevenueLabel>Outstanding Balance</RevenueLabel>
          </RevenueCard>
          <RevenueCard padding="lg">
            <RevenueValue>{isLoading ? '—' : (data?.expiring_entitlements_30d ?? 0)}</RevenueValue>
            <RevenueLabel>Entitlements Expiring (30d)</RevenueLabel>
          </RevenueCard>
        </RevenueHighlight>
      </Fade>

      <Fade triggerOnce cascade damping={0.1}>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index} padding="md">
              <StatIcon $color={stat.color}>{stat.icon}</StatIcon>
              <StatContent>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
                {stat.change && <StatChange $positive={stat.positive}>{stat.change}</StatChange>}
              </StatContent>
            </StatCard>
          ))}
        </StatsGrid>
      </Fade>

      <ContentGrid>
        <MainColumn>
          <Slide triggerOnce direction="up">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Recent Payments</CardTitle>
              </CardHeader>
              <ActivityList>
                {isLoading && <p style={{ color: '#64748b', padding: '8px 0' }}>Loading…</p>}
                {!isLoading && (data?.recent_payments ?? []).length === 0 && (
                  <p style={{ color: '#64748b', padding: '8px 0' }}>No recent payments.</p>
                )}
                {(data?.recent_payments ?? []).map((p) => (
                  <ActivityItem key={p.payment_id}>
                    <ActivityIcon $type="payment">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                    </ActivityIcon>
                    <ActivityContent>
                      <ActivityTitle>{p.student_name ?? 'Unknown student'}</ActivityTitle>
                      <ActivityDescription>
                        {formatAmount(p.amount)} — {p.invoice_number ?? 'payment'}
                      </ActivityDescription>
                    </ActivityContent>
                    <ActivityTime>{formatRelativeTime(p.paid_at)}</ActivityTime>
                  </ActivityItem>
                ))}
              </ActivityList>
            </Card>
          </Slide>
        </MainColumn>

        <SideColumn>
          <Slide triggerOnce direction="right">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Upcoming Sessions</CardTitle>
              </CardHeader>
              <SubjectList>
                {isLoading && <p style={{ color: '#64748b', padding: '8px 0' }}>Loading…</p>}
                {!isLoading && (data?.upcoming_sessions ?? []).length === 0 && (
                  <p style={{ color: '#64748b', padding: '8px 0' }}>No upcoming sessions.</p>
                )}
                {(data?.upcoming_sessions ?? []).map((s) => (
                  <SubjectItem key={s.session_id}>
                    <SubjectName>{s.subject_name}</SubjectName>
                    <SubjectStats>
                      <span>{s.class_name}</span>
                      {s.teacher_name && <span>{s.teacher_name}</span>}
                    </SubjectStats>
                  </SubjectItem>
                ))}
              </SubjectList>
            </Card>
          </Slide>
        </SideColumn>
      </ContentGrid>

      <ContentGrid2>
        <PendingPaymentsWidget report={pendingPayments} isLoading={pendingPaymentsLoading} mode="summary" />
        <AttendanceConcernsWidget />
      </ContentGrid2>

      <ContentGrid2>
        <StaffLeaveWidget />
      </ContentGrid2>

      <DigestGrid data-testid="candeza-dash-digest-grid">
        {!isAllMode && singleDigest.data && <DailyStatusDigestCard data={singleDigest.data} />}
        {isAllMode &&
          fanOutDigests.map((d) => (
            <DailyStatusDigestCard key={d.tenant_id} data={d} testId={`candeza-dash-digest-${d.tenant_id}`} />
          ))}
        {isAllMode && consolidatedDigest && (
          <DailyStatusDigestCard
            data={consolidatedDigest}
            title="All Centres — Consolidated"
            testId="candeza-dash-digest-consolidated"
          />
        )}
      </DigestGrid>
    </PageContainer>
  );
};

export default OwnerDashboard;

